<template>
    <div class="main-content px-4">
        <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1060">
            <transition-group name="slide-fade">
                <div v-for="alert in alerts" :key="alert.id"
                    class="toast show align-items-center border-0 shadow-lg mb-2"
                    :class="'bg-' + alert.type + '-subtle text-' + alert.type" role="alert">
                    <div class="d-flex p-3">
                        <i :class="alert.icon" class="me-3 fs-5"></i>
                        <div class="toast-body p-0 fw-medium">
                            {{ alert.message }}
                        </div>
                        <button type="button" class="btn-close ms-auto" @click="removeAlert(alert.id)"></button>
                    </div>
                </div>
            </transition-group>
        </div>
        <header class="content-header mb-2 mt-n5">
            <nav aria-label="breadcrumb">
                <ol class="breadcrumb mb-0">
                    <li class="breadcrumb-item text-dark"> <router-link class="nav-link active" to="/">Início</router-link></li>
                    <li class="breadcrumb-item active text-dark">Inventários</li>
                </ol>
            </nav>
        </header>

        <div class="card p-4 shadow-subtle rounded-4 border-0">
            <div class="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <div>
                    <h4 class="fw-bold text-dark mb-0">Inventários e Balanços</h4>
                    <span class="text-muted small">Gerencie as sessões de auditoria de estoque</span>
                </div>
                <button class="btn btn-pmg-primary px-4" @click="openModal">
                    <i class="bi bi-plus-lg me-2"></i>Novo Inventário
                </button>
            </div>

            <div v-if="loading" class="text-center py-5">
                <div class="spinner-border text-primary" role="status"></div>
                <p class="text-muted mt-2 fw-bold">A carregar inventários...</p>
            </div>

            <div v-else class="table-responsive">
                <table class="table table-pmg align-middle mb-0">
                    <thead>
                        <tr>
                            <th class="ps-4">REFERÊNCIA / DATA</th>
                            <th>UNIDADE (FILIAL)</th>
                            <th class="text-center">STATUS</th>
                            <th class="text-end pe-4">AÇÕES</th>
                        </tr>
                    </thead>
                    <tbody v-if="inventories.length > 0">
                        <tr v-for="inv in inventories" :key="inv.id">
                            <td class="ps-4">
                                <div class="fw-bold text-dark">{{ inv.description }}</div>
                                <div class="text-muted small">{{ formatarDataBR(inv.created_at) }}</div>
                            </td>
                            <td>{{ inv.branch?.name || 'Não informada' }}</td>
                            <td class="text-center">
                                <span :class="getStatusBadge(inv.status)">{{ translateStatus(inv.status) }}</span>
                            </td>
                            <td class="text-end pe-4">
                                <router-link :to="'/inventory/' + inv.id + '/count'" class="btn btn-pmg-icon border"
                                    :title="inv.status === 'FROZEN' ? 'Continuar auditoria' : 'Visualizar resultado'">

                                    <template v-if="inv.status === 'FROZEN'">
                                        <i class="bi bi-upc-scan me-1 text-primary"></i> Auditar Estoque
                                    </template>

                                    <template v-else>
                                        <i class="bi bi-eye me-1 text-muted"></i> Ver Detalhes
                                    </template>

                                </router-link>
                            </td>
                        </tr>
                    </tbody>
                    <tbody v-else>
                        <tr>
                            <td colspan="4" class="text-center py-5 text-muted">
                                <i class="bi bi-inbox fs-1 mb-2 opacity-50 d-block"></i>
                                Nenhum inventário registrado até o momento.
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div class="modal fade" id="modalOpenInventory" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content rounded-4 border-0 shadow-lg">
                    <div class="modal-header border-0 p-4 pb-0">
                        <h5 class="fw-bold m-0 text-dark">Iniciar Novo Inventário</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" @click="closeModal"></button>
                    </div>
                    <div class="modal-body p-4">
                        <div class="alert alert-warning border-0 rounded-3 small mb-4 d-flex align-items-start">
                            <i class="bi bi-exclamation-triangle-fill fs-5 me-3 mt-1"></i>
                            <span>
                                <strong>Atenção:</strong> Esta ação irá "congelar" o saldo atual de todos os produtos da
                                filial selecionada para iniciar a auditoria.
                            </span>
                        </div>

                        <div class="mb-3">
                            <label class="form-label small fw-bold text-muted">Unidade para Auditoria</label>
                            <select v-model="newInv.branch_id" class="form-select form-control-pmg">
                                <option value="" disabled>Selecione a filial...</option>
                                <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
                            </select>
                        </div>
                        <div class="mb-4">
                            <label class="form-label small fw-bold text-muted">Descrição / Motivo</label>
                            <input v-model="newInv.description" type="text" class="form-control form-control-pmg"
                                placeholder="Ex: Balanço Semestral - Geral">
                        </div>

                        <div class="d-flex gap-2">
                            <button class="btn btn-pmg-light w-50 py-3" @click="closeModal">Cancelar</button>
                            <button class="btn btn-pmg-primary w-50 py-3" @click="createInventory"
                                :disabled="isCreating || !newInv.branch_id">
                                <span v-if="isCreating" class="spinner-border spinner-border-sm me-2"></span>
                                CONGELAR E INICIAR
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/services/api'; // Ajuste o caminho da API conforme seu projeto

export default {
    name: 'InventoryList',
    data() {
        return {
            inventories: [],
            branches: [],
            loading: false,
            isCreating: false,
            newInv: {
                branch_id: '',
                description: ''
            },
            alerts: []
        }
    },
    mounted() {
        this.fetchInventories();
        this.fetchBranches();
    },
    methods: {
        async fetchInventories() {
            this.loading = true;
            try {
                const res = await api.get('/inventories');
                this.inventories = res.data.data || res.data;
            } catch (error) {
                console.error("Erro ao carregar inventários:", error);
            } finally {
                this.loading = false;
            }
        },
        async fetchBranches() {
            try {
                const res = await api.get('/branches');
                this.branches = res.data.data || res.data;
            } catch (error) {
                console.error("Erro ao carregar filiais:", error);
            }
        },
        openModal() {
            this.newInv = { branch_id: '', description: '' };
            const modal = new window.bootstrap.Modal(document.getElementById('modalOpenInventory'));
            modal.show();
        },
        closeModal() {
            const modalEl = document.getElementById('modalOpenInventory');
            if (modalEl) {
                const modalInstance = window.bootstrap.Modal.getInstance(modalEl);
                if (modalInstance) modalInstance.hide();
            }
        },
        async createInventory() {
            if (!this.newInv.branch_id) {
                // Substituído o alert() nativo
                this.showAlert("Selecione uma unidade para congelar o estoque.", "danger");
                return;
            }
            if (!this.newInv.description) {
                this.newInv.description = `Inventário - ${new Date().toLocaleDateString('pt-BR')}`;
            }

            this.isCreating = true;
            try {
                const res = await api.post('/inventories', this.newInv);
                this.closeModal();

                const newId = res.data.data ? res.data.data.id : res.data.id;
                this.$router.push(`/inventory/${newId}/count`);
            } catch (e) {
                if (e.response?.status === 422) {
                    const validationErrors = e.response.data?.data || e.response.data?.errors;
                    this.errors = validationErrors || {};
                    if (!validationErrors || Object.keys(validationErrors).length === 0) {
                        this.showAlert("Erro de validação. Verifique os campos preenchidos.", "error");
                        return;
                    }
                    let mensagensErro = "Os seguintes campos precisam de correção:\n\n";
                    for (const campo in validationErrors) {
                        mensagensErro += `- ${validationErrors[campo][0]}\n`;
                    }

                    this.showAlert(mensagensErro, "error");

                } else {
                    console.error("Erro desconhecido:", e);
                    this.showAlert("Erro ao iniciar inventário. Verifique sua conexão ou a disponibilidade do servidor", "error");
                }
            } finally {
                this.isCreating = false;
            }
        },
        getStatusBadge(status) {
            const styles = {
                'FROZEN': 'badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-2',
                'ADJUSTED': 'badge bg-success-subtle text-success border border-success-subtle px-3 py-2',
                'CANCELLED': 'badge bg-secondary-subtle text-secondary border border-secondary-subtle px-3 py-2',
                'OPEN': 'badge bg-warning-subtle text-warning border border-warning-subtle px-3 py-2'
            };
            return styles[status] || 'badge bg-light text-dark border px-3 py-2';
        },
        formatarDataBR(data) {
            if (!data) return '-';
            return new Date(data).toLocaleString('pt-BR');
        },
        showAlert(message, type = 'success') {
            const id = Date.now();
            let icon = type === 'danger' ? 'bi-exclamation-circle-fill' : 'bi-check-circle-fill';
            this.alerts.push({ id, message, type, icon });
            setTimeout(() => this.removeAlert(id), 8000);
        },

        removeAlert(id) {
            this.alerts = this.alerts.filter(a => a.id !== id);
        },

        translateStatus(status) {
            const map = {
                'FROZEN': 'Congelado',
                'ADJUSTED': 'Ajustado',
                'OPEN': 'Aberto',
                'CANCELLED': 'Cancelado'
            };
            return map[status] || status;
        },
    }
}
</script>

<style scoped>
.main-content {
    background: #f8fafc;
    min-height: 100vh;
    padding: 2rem;
}

.shadow-subtle {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.04), 0 4px 6px -2px rgba(0, 0, 0, 0.02) !important;
}

.form-control-pmg {
    border-radius: 10px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    transition: all 0.2s;
    padding: 0.8rem;
}

.form-control-pmg:focus {
    background: #fff;
    border-color: #561569;
    box-shadow: 0 0 0 3px rgba(33, 51, 97, 0.1);
}

.btn-pmg-primary {
    background: #561569;
    color: white;
    border-radius: 10px;
    font-weight: 600;
    padding: 0.6rem 1.2rem;
}

.btn-pmg-light {
    background: white;
    color: #64748b;
    border-radius: 10px;
    font-weight: 600;
    padding: 0.6rem 1.2rem;
    border: 1px solid #e2e8f0;
}

.btn-pmg-icon {
    background: #f1f5f9;
    border: none;
    color: #475569;
    border-radius: 8px;
    padding: 6px 12px;
    display: inline-block;
    font-weight: 600;
    text-decoration: none;
}

.btn-pmg-icon:hover {
    background: #e2e8f0;
    color: #561569;
}

.table-pmg thead th {
    background: #f8fafc;
    color: #64748b;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 1.2rem;
}

.table-pmg tbody td {
    padding: 1.2rem;
    border-bottom: 1px solid #f1f5f9;
}


.nav-link.active {
    font-weight: 600;
    color: #64748b !important;
}
</style>